/**
 * Sentry initialization for web platform.
 *
 * IMPORTANT: This module calls Sentry.init() at load time to ensure it runs
 * before Sentry.wrap() is evaluated. Import this as early as possible in _layout.tsx.
 */
import * as Sentry from '@sentry/react';

import { env } from './env';
import { getBuildInformation } from './getBuildInformation';
import { isSentryNoiseEvent } from './sentryNoiseFilter';

const isProduction = env.EXPO_PUBLIC_APP_ENV === 'production';

/** Runtime toggle for Sentry instrumentation. */
const isSentryEnabled = env.EXPO_PUBLIC_SENTRY_ENABLED !== 'false';

const commitSha = process.env.EXPO_PUBLIC_GIT_COMMIT_SHA?.substring?.(0, 7) || '';

const baseIntegrations = [
  Sentry.browserTracingIntegration(),
  // Capture console.error calls to catch React errors that bypass error boundaries
  // (e.g., "Maximum update depth exceeded")
  Sentry.captureConsoleIntegration({ levels: ['error'] }),
];

Sentry.init({
  release: `${commitSha}-web`,
  dist: commitSha,
  enabled: isSentryEnabled,
  dsn: env.EXPO_PUBLIC_SENTRY_DSN,
  tracesSampleRate: isProduction ? 0.1 : 1.0,
  tracePropagationTargets: [env.EXPO_PUBLIC_BASE_API_URL],
  replaysSessionSampleRate: isProduction ? 0.1 : 0,
  replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
  environment: env.EXPO_PUBLIC_APP_ENV,
  integrations:
    env.EXPO_PUBLIC_APP_ENV !== 'local' && env.EXPO_PUBLIC_APP_ENV !== 'development'
      ? [
          ...baseIntegrations,
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ]
      : baseIntegrations,
  beforeSend: (event) => (isSentryNoiseEvent(event) ? null : event),
});

// Set build information tags
const scope = Sentry.getGlobalScope();

getBuildInformation().forEach((item) => {
  scope.setTag(item.key, item.value);
});

/**
 * Navigation integration placeholder for web.
 * Web uses browser-based tracking, not React Navigation.
 */
export const navigationIntegration = null;

/**
 * Extended Sentry object with wrap function for web compatibility.
 * On native, Sentry.wrap tracks app start and touch events.
 * On web, we use withProfiler for similar component tracking.
 */
export const SentryWithWrap = {
  ...Sentry,
  wrap: Sentry.withProfiler,
};

export { SentryWithWrap as Sentry };
